import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import ReviewModal from "@/components/ReviewModal";
import CrewReviewModal from "@/components/CrewReviewModal";
import ABNValidationModal from "@/components/ABNValidationModal";
import WelcomeModal from "@/components/WelcomeModal";
import PendingApprovalModal from "@/components/PendingApprovalModal";
import CancellationPaymentBlockerModal from "@/components/CancellationPaymentBlockerModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Search,
  Users,
  Briefcase,
  Calendar,
  TrendingUp,
  ChevronRight,
  FileText,
  Award,
  UserCheck,
  ArrowUpRight,
  Clock,
  CheckCircle,
  X,
  DollarSign,
  MapPin,
  AlertCircle,
  Loader,
  Smartphone,
  Bell,
  Plus,
  ArrowRight,
  AlertTriangle,
  Sun,
  Cloud,
  CloudSun,
  CloudRain,
  CloudSnow,
  CloudLightning,
  CloudFog,
  Sunset,
  Droplets,
  Camera
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationDropdown from "@/components/NotificationDropdown";
import "@/styles/commandDeck.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Weather helpers (mirror mobile/app/(tabs)/dashboard.tsx) ─────────────
// Format an ISO datetime → "5:12 PM"
const formatWeatherTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
};

// Open-Meteo WMO weather_code → human label
const getWeatherLabel = (code) => {
  if (code === 0) return "Clear Sky";
  if (code <= 2) return "Partly Cloudy";
  if (code === 3) return "Overcast";
  if (code <= 48) return "Foggy";
  if (code <= 57) return "Drizzle";
  if (code <= 67) return "Rain";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Rain Showers";
  if (code <= 86) return "Snow Showers";
  if (code >= 95) return "Thunderstorm";
  return "Cloudy";
};

// Open-Meteo WMO weather_code → lucide-react icon component
const getWeatherLucide = (code) => {
  if (code === 0) return Sun;
  if (code <= 2) return CloudSun;
  if (code <= 44) return Cloud;
  if (code <= 48) return CloudFog;
  if (code <= 67) return CloudRain;
  if (code <= 77) return CloudSnow;
  if (code <= 82) return CloudRain;
  if (code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
};

const getWeatherDayName = (dateStr, index) => {
  if (index === 0) return "Today";
  if (index === 1) return "Tomorrow";
  return new Date(dateStr).toLocaleDateString("en-AU", { weekday: "short" });
};

// Compact strip label, e.g. "Today" / "Sat 27"
const getWeatherStripDay = (dateStr, index) => {
  if (index === 0) return "Today";
  const d = new Date(dateStr);
  return `${d.toLocaleDateString("en-AU", { weekday: "short" })} ${d.getDate()}`;
};

// Light-quality dot colour from rain chance
const weatherLightColor = (rain) => (rain < 25 ? "#67C6B4" : rain < 60 ? "#FCD34D" : "#F87171");

// Subtle card backdrop tint reflecting today's conditions (low alpha → text stays legible)
const weatherBg = (code) => {
  if (code <= 1) return "radial-gradient(680px 300px at 82% -15%, rgba(252,211,77,.14), transparent 60%)"; // clear / sunny — warm glow
  if (code === 2) return "radial-gradient(680px 300px at 82% -15%, rgba(252,211,77,.08), transparent 58%)"; // partly cloudy
  if (code === 3 || (code >= 45 && code <= 48)) return "linear-gradient(180deg, rgba(148,163,184,.12), transparent 48%)"; // overcast / fog — muted grey
  if (code >= 95) return "radial-gradient(720px 320px at 50% -20%, rgba(124,58,237,.20), transparent 60%)"; // thunderstorm — violet
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "radial-gradient(720px 320px at 50% -20%, rgba(186,230,253,.13), transparent 62%)"; // snow — cool
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "radial-gradient(720px 320px at 50% -20%, rgba(96,165,250,.16), transparent 62%)"; // rain / drizzle / showers — blue
  return "transparent";
};

const DashboardRedesigned = ({ onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentReview, setCurrentReview] = useState(null);
  const [showABNModal, setShowABNModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showPendingApprovalModal, setShowPendingApprovalModal] = useState(false);
  const [outstandingCancellationFees, setOutstandingCancellationFees] = useState(null);
  const [showCancellationBlocker, setShowCancellationBlocker] = useState(false);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [showPendingOfferModal, setShowPendingOfferModal] = useState(false);
  const [currentPendingOffer, setCurrentPendingOffer] = useState(null);
  const [offerActionLoading, setOfferActionLoading] = useState(false);
  const [offerDeclined, setOfferDeclined] = useState(false);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [pendingCrewReviews, setPendingCrewReviews] = useState([]);
  const [showCrewReviewModal, setShowCrewReviewModal] = useState(false);
  const [currentCrewReview, setCurrentCrewReview] = useState(null);
  const [systemModalMessages, setSystemModalMessages] = useState([]);
  const [showSystemModal, setShowSystemModal] = useState(false);
  // Weather: null = loading, { noCoords: true } = no location, { location, days } = ready
  const [weather, setWeather] = useState(null);
  const { isDark } = useTheme();
  const isAdminViewing = localStorage.getItem('admin_viewing_as_user');
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  useEffect(() => {
    console.log('🚀 Dashboard useEffect triggered', {
      userId: user.id,
      isAdminViewing,
      hasUserId: !!user.id
    });

    // Check user status and show appropriate modals
    const checkUserStatus = async () => {
      if (!user.id) {
        console.log('❌ No user.id, skipping status check');
        return;
      }

      console.log('🔍 Fetching user status from API...');

      // Handle payment verification FIRST (before checking outstanding fees)
      // This runs when user is redirected back from Stripe checkout
      const urlParams = new URLSearchParams(window.location.search);
      const cancellationPaid = urlParams.get('cancellation_paid');
      if (cancellationPaid) {
        try {
          const verifyResponse = await axios.post(
            `${API}/cancellation-invoices/verify-payment`,
            { invoice_id: cancellationPaid }
          );
          if (verifyResponse.data.success) {
            const { toast } = await import('sonner');
            toast.success('Payment received! Your access has been restored.');
          }
        } catch (err) {
          console.error('Failed to verify cancellation payment:', err);
        }
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Now check for outstanding cancellation fees (highest priority blocker)
      try {
        const feesResponse = await axios.get(
          `${API}/user/outstanding-cancellation-fees`,
          { params: { user_email: user.email } }
        );
        if (feesResponse.data.has_outstanding_fees) {
          setOutstandingCancellationFees(feesResponse.data);
          setShowCancellationBlocker(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check cancellation fees:', err);
      }

      try {
        const response = await axios.get(`${API}/profile/${user.id}`);
        const latestUser = response.data;

        console.log('📊 Dashboard - User status check:', {
          userId: user.id,
          status: latestUser.status,
          statusType: typeof latestUser.status,
          fullUser: latestUser,
          isAdminViewing
        });

        // Get the user status (check both status and approval_status fields)
        const userStatus = (latestUser.status || latestUser.approval_status || '').toUpperCase();
        console.log('🔍 Checking status:', { userStatus });

        // Show appropriate modal based on user status
        if (userStatus === 'NEW') {
          console.log('🎉 NEW user detected, showing WelcomeModal');
          setShowWelcomeModal(true);
          return;
        }

        if (userStatus === 'PENDING') {
          console.log('⏳ PENDING user detected, showing PendingApprovalModal');
          setShowPendingApprovalModal(true);
          return;
        }

        // For approved users, check for ABN modal - require validation for ALL users on new system
        // Accept either abn_validated_new_system OR abn_verified as proof of validation
        if (userStatus === 'APPROVED' && !latestUser.abn_validated_new_system && !latestUser.abn_verified) {
          console.log('✅ Showing ABN modal - user has not validated ABN');
          setShowABNModal(true);
        }
      } catch (error) {
        console.error('❌ Error checking user status:', error);
      }
    };

    checkUserStatus();
    
    // Fetch profile photo
    const fetchProfilePhoto = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${API}/profile/${user.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_token')}` },
          });
          if (response.data.profile_photo_url) {
            setProfilePhotoUrl(response.data.profile_photo_url);
          }
          const cover = response.data.portfolio_images?.[0] || null;
          if (cover) setCoverPhoto(cover);
        } catch (error) {
          console.error('Error fetching profile photo:', error);
        }
      }
    };
    
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          params: { user_email: user.email || 'test@example.com' }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats", error);
        setStats({
          total_applications: 0,
          upcoming_jobs: 0,
          total_crew_members: 0,
          member_since: 'N/A',
          active_jobs_posted: 0
        });
      } finally {
        setLoading(false);
      }
    };

    const checkPendingReviews = async () => {
      try {
        if (!user.id) return;

        const response = await axios.get(`${API}/reviews/pending/${user.id}`);
        if (response.data.success && response.data.pending_reviews.length > 0) {
          // Filter for client reviews only (job owner reviewing crew) - exclude crew reviews
          const clientReviews = response.data.pending_reviews.filter(r => r.reviewer_type !== 'crew');

          if (clientReviews.length > 0) {
            setPendingReviews(clientReviews);
            setCurrentReview(clientReviews[0]);
            setShowReviewModal(true);
          }
        }
      } catch (error) {
        console.error("Failed to check pending reviews", error);
      }
    };

    const checkPendingOffers = async () => {
      try {
        if (!user.id) return;

        console.log('🔍 Checking pending offers for user:', user.id);
        const response = await axios.get(`${API}/offers/pending/${user.id}`);
        console.log('📬 Pending offers response:', response.data);

        if (response.data.success && response.data.pending_offers && response.data.pending_offers.length > 0) {
          setPendingOffers(response.data.pending_offers);
          setCurrentPendingOffer(response.data.pending_offers[0]);
          setShowPendingOfferModal(true);
        }
      } catch (error) {
        console.error("Failed to check pending offers", error);
      }
    };

    // Check for pending crew reviews (crew members need to review job owners)
    const checkPendingCrewReviews = async () => {
      try {
        if (!user.id) return;

        console.log('🔍 Checking pending crew reviews for user:', user.id);
        const response = await axios.get(`${API}/reviews/pending/${user.id}`);
        console.log('📝 Pending crew reviews response:', response.data);

        if (response.data.success && response.data.pending_reviews && response.data.pending_reviews.length > 0) {
          // Filter for crew reviews only (crew member reviewing a client/job owner)
          const crewReviews = response.data.pending_reviews.filter(r => r.reviewer_type === 'crew');
          console.log('📝 Filtered crew reviews:', crewReviews);

          if (crewReviews.length > 0) {
            setPendingCrewReviews(crewReviews);
            setCurrentCrewReview(crewReviews[0]);
            setShowCrewReviewModal(true);
          }
        }
      } catch (error) {
        console.error("Failed to check pending crew reviews", error);
      }
    };

    const fetchModalSystemMessages = async () => {
      try {
        if (!user.id) return;
        const response = await axios.get(`${API}/system-messages/modal`, {
          params: { user_id: user.id }
        });
        if (response.data.success && response.data.messages.length > 0) {
          setSystemModalMessages(response.data.messages);
          setShowSystemModal(true);
        }
      } catch (error) {
        console.error('Error fetching modal system messages:', error);
      }
    };

    fetchProfilePhoto();
    fetchStats();
    checkPendingReviews();
    checkPendingOffers();
    checkPendingCrewReviews();
    fetchModalSystemMessages();
  }, [user.id, isAdminViewing]);

  // Fetch shoot weather once we know the user's coordinates.
  // Coords come from the profile (location_lat/lon → latitude/longitude),
  // falling back to the cached auth `user` object. No coords → graceful empty state.
  useEffect(() => {
    let cancelled = false;

    const loadWeather = async () => {
      try {
        let lat;
        let lon;
        let locationName;

        if (user.id) {
          try {
            const { data: p } = await axios.get(`${API}/profile/${user.id}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_token')}` },
            });
            lat = p.location_lat ?? p.latitude ?? p.address_lat;
            lon = p.location_lon ?? p.longitude ?? p.address_lon;
            locationName = p.suburb || p.city || p.location_city || p.location;
          } catch (err) {
            // ignore — fall back to cached auth user fields
          }
        }

        if (lat == null || lon == null) {
          lat = user.location_lat ?? user.latitude;
          lon = user.location_lon ?? user.longitude;
          locationName = locationName || user.suburb || user.city || user.location_city;
        }

        if (lat == null || lon == null) {
          if (!cancelled) setWeather({ noCoords: true });
          return;
        }

        // Server-side weather (Google Weather API, Open-Meteo fallback) — keeps the
        // API key protected, caches, and returns a normalized shape.
        const res = await axios.get(`${API}/weather`, { params: { lat, lon, days: 5 } });
        const data = res.data || {};
        const days = (data.days || []).map((d, i) => {
          const sunset = d.sunset;
          // Evening golden hour ≈ the hour before sunset
          let goldenHour;
          if (sunset) {
            const gh = new Date(sunset);
            gh.setMinutes(gh.getMinutes() - 60);
            goldenHour = gh.toISOString();
          }
          return {
            date: d.date,
            dayName: getWeatherDayName(d.date, i),
            stripDay: getWeatherStripDay(d.date, i),
            tempMax: d.tempMax,
            tempMin: d.tempMin,
            weatherCode: d.weatherCode,
            condition: d.condition || "",
            rainChance: d.rainChance || 0,
            sunset,
            goldenHour,
          };
        });
        // Today shows the LIVE current temperature/conditions (more accurate than the daily max).
        if (days[0] && data.current && data.current.temp != null) {
          days[0] = {
            ...days[0],
            tempMax: data.current.temp,
            condition: data.current.condition || days[0].condition,
            weatherCode: data.current.weatherCode != null ? data.current.weatherCode : days[0].weatherCode,
          };
        }

        if (!cancelled) setWeather({ location: locationName || "Your location", days });
      } catch (error) {
        console.error("Failed to fetch weather:", error);
        if (!cancelled) setWeather({ noCoords: true });
      }
    };

    loadWeather();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const handleABNValidated = useCallback(() => {
    setShowABNModal(false);
    window.location.reload();
  }, []);

  const handleDismissSystemModal = async () => {
    if (systemModalMessages.length === 0) return;
    const currentMsg = systemModalMessages[0];
    try {
      await axios.put(`${API}/notifications/${currentMsg.id}/read`, null, {
        params: { user_id: user.id }
      });
    } catch (error) {
      console.error('Error dismissing system message:', error);
    }
    const remaining = systemModalMessages.slice(1);
    setSystemModalMessages(remaining);
    if (remaining.length === 0) {
      setShowSystemModal(false);
    }
  };

  const handleReviewSubmitSuccess = () => {
    const remaining = pendingReviews.filter(r => r.id !== currentReview.id);
    setPendingReviews(remaining);

    if (remaining.length > 0) {
      setCurrentReview(remaining[0]);
      setShowReviewModal(true);
    } else {
      setShowReviewModal(false);
      setCurrentReview(null);
    }
  };

  const handleCrewReviewSubmitSuccess = () => {
    const remaining = pendingCrewReviews.filter(r => r.id !== currentCrewReview.id);
    setPendingCrewReviews(remaining);

    if (remaining.length > 0) {
      setCurrentCrewReview(remaining[0]);
      setShowCrewReviewModal(true);
    } else {
      setShowCrewReviewModal(false);
      setCurrentCrewReview(null);
    }
  };

  // Format date for offer display
  const formatOfferDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format currency for offer display
  const formatOfferCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate time remaining for offer
  const getOfferTimeRemaining = (expiresAt) => {
    if (!expiresAt) return { hours: 0, minutes: 0, expired: true };
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return { hours: 0, minutes: 0, expired: true };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes, expired: false };
  };

  // Handle accept offer from modal
  const handleAcceptOffer = async () => {
    if (!currentPendingOffer?.token) return;

    setOfferActionLoading(true);
    try {
      const response = await axios.post(`${API}/offers/accept?token=${currentPendingOffer.token}`);
      if (response.data.success) {
        // Show accepted success state
        setOfferAccepted(true);
      }
    } catch (error) {
      console.error('Failed to accept offer:', error);
      alert(error.response?.data?.detail || 'Failed to accept offer. Please try again.');
    } finally {
      setOfferActionLoading(false);
    }
  };

  // Handle closing the accepted modal and redirect to upcoming bookings
  const handleAcceptedModalClose = () => {
    setOfferAccepted(false);
    setShowPendingOfferModal(false);
    setCurrentPendingOffer(null);
    navigate('/upcoming-bookings');
  };

  // Handle decline offer from modal
  const handleDeclineOffer = async () => {
    if (!currentPendingOffer?.token) return;

    setOfferActionLoading(true);
    try {
      const response = await axios.post(`${API}/offers/decline?token=${currentPendingOffer.token}`);
      if (response.data.success) {
        // Show declined success state
        setOfferDeclined(true);
      }
    } catch (error) {
      console.error('Failed to decline offer:', error);
      // Check if already declined
      const errorMsg = error.response?.data?.detail || '';
      if (errorMsg.includes('already declined')) {
        setOfferDeclined(true);
      } else {
        alert(errorMsg || 'Failed to decline offer. Please try again.');
      }
    } finally {
      setOfferActionLoading(false);
    }
  };

  // Handle closing the declined modal and redirect to dashboard
  const handleDeclinedModalClose = () => {
    setOfferDeclined(false);
    setShowPendingOfferModal(false);
    setCurrentPendingOffer(null);
    // Remove from pending offers list
    const remaining = pendingOffers.filter(o => o.id !== currentPendingOffer?.id);
    setPendingOffers(remaining);
    // Refresh the page to update dashboard
    window.location.reload();
  };

  // ---- Command Deck derived display values ------------------------------
  const firstName = user.first_name || user.firstName || user.full_name?.split(' ')[0] || user.fullName?.split(' ')[0] || 'there';
  const dashInitials = (((user.first_name || user.firstName || '')[0] || '') + ((user.last_name || user.lastName || '')[0] || '')).toUpperCase() || (firstName[0] || 'U').toUpperCase();
  const todayLabel = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' }).toUpperCase();
  // "Applications out" = applications still pending on jobs that are still seeking
  // applicants (matches My Applications "Active"), not lifetime applications made.
  const applicationsCount = stats?.applications_out ?? 0;
  const upcomingCount = stats?.upcoming_jobs ?? 0;
  const jobPostsCount = stats?.jobs_posted ?? stats?.active_jobs_posted ?? 0;
  const crewNetworkCount = stats?.total_crew_members ?? 0;
  const hasPendingOffer = pendingOffers.length > 0 && !!currentPendingOffer;
  const offerTime = currentPendingOffer ? getOfferTimeRemaining(currentPendingOffer.expires_at) : null;
  const thingsNeedingYou = pendingOffers.length;

  // ---- Weather derived display values -----------------------------------
  const weatherReady = weather && Array.isArray(weather.days) && weather.days.length > 0;
  const weatherToday = weatherReady ? weather.days[0] : null;
  const weatherTodayRain = weatherToday?.rainChance ?? 0;
  const weatherLightTag = weatherTodayRain < 25
    ? { text: "Good to shoot", fg: "#67C6B4" }
    : weatherTodayRain < 60
      ? { text: "Possible showers", fg: "#FCD34D" }
      : { text: "Likely rain", fg: "#F87171" };
  const WeatherTodayIcon = weatherToday ? getWeatherLucide(weatherToday.weatherCode) : Sun;

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">
          <style>{`
            /* Full-bleed welcome hero: break out of .cd-main padding so it stays
               flush to the page edges at EVERY breakpoint. The horizontal margin
               must mirror .cd-main's horizontal padding, which is 16px ≤720px and
               40px above that — otherwise mid-width screens get side gaps. */
            .cd-hero-bleed { margin: -22px -16px 24px; }
            @media (min-width: 721px) { .cd-hero-bleed { margin-left: -40px; margin-right: -40px; } }
            /* Desktop: .cd-main is capped at max-width:1180px and centred inside the
               sidebar-offset content area, so on wide screens gradient gutters appear
               beside the hero. Stretch the hero to fill the WHOLE content area (sidebar
               edge -> right viewport edge) at every width. min(-40px, …) keeps the plain
               -40px bleed until the 1180px cap actually bites. */
            @media (min-width: 1024px) {
              .cd-hero-bleed {
                margin-top: -44px;
                margin-bottom: 28px;
                margin-left: min(-40px, calc(-1 * ((100vw - var(--sidebar-width, 240px) - 1180px) / 2 + 40px)));
                margin-right: min(-40px, calc(-1 * ((100vw - var(--sidebar-width, 240px) - 1180px) / 2 + 40px)));
              }
            }
            /* Mobile: clear the fixed top bar and give the hero copy breathing room
               so it isn't jammed against the top edge; keep the cover photo showcased. */
            @media (max-width: 720px) {
              .cd-hero-bleed {
                padding: 64px 22px 30px !important;
                min-height: 340px !important;
                justify-content: flex-end !important;
              }
            }
          `}</style>
          {/* WELCOME FEATURE BANNER */}
          <div
            className="sz-col-mobile cd-hero-bleed"
            style={{ position: 'relative', borderRadius: 0, padding: '40px 40px 44px', minHeight: 360, background: 'linear-gradient(120deg,rgba(40,192,208,.18),rgba(87,43,124,.52) 55%,rgba(120,40,124,.42))', borderBottom: '1px solid rgba(255,255,255,.1)', boxShadow: '0 26px 60px -34px rgba(0,0,0,.8)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}
          >
            {coverPhoto && (
              <>
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, backgroundImage: `url(${coverPhoto})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, background: 'linear-gradient(120deg,rgba(40,192,208,.14),rgba(87,43,124,.42) 55%,rgba(120,40,124,.34))' }} />
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, background: 'linear-gradient(90deg, rgba(12,17,32,.7), rgba(12,17,32,.36) 55%, rgba(12,17,32,.18))' }} />
              </>
            )}
            <div style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10, flexWrap: 'wrap' }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>{todayLabel}</span>
                {thingsNeedingYou > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#FDE68A', background: 'rgba(180,83,9,.3)', padding: '5px 11px', borderRadius: 999 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: '#FDE68A' }} />
                    {thingsNeedingYou} thing{thingsNeedingYou > 1 ? 's' : ''} need{thingsNeedingYou > 1 ? '' : 's'} you
                  </span>
                )}
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 36, letterSpacing: '-1.2px', lineHeight: 1.02, color: '#fff' }}>
                Welcome back, <span style={{ background: 'linear-gradient(110deg,#67C6B4,#28C0D0 45%,#C98AD4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{firstName}</span>
              </div>
              <div style={{ display: 'flex', gap: 22, marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.82)', fontSize: 13.5 }}>
                  <FileText style={{ width: 16, height: 16, color: '#28C0D0' }} />
                  <span style={{ color: '#fff', fontWeight: 700 }}>{loading ? '…' : applicationsCount}</span>&nbsp;applications out
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.82)', fontSize: 13.5 }}>
                  <Calendar style={{ width: 16, height: 16, color: '#67C6B4' }} />
                  <span style={{ color: '#fff', fontWeight: 700 }}>{loading ? '…' : upcomingCount}</span>&nbsp;upcoming jobs
                </div>
                {hasPendingOffer && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.82)', fontSize: 13.5 }}>
                    <DollarSign style={{ width: 16, height: 16, color: '#FDE68A' }} />
                    <span style={{ color: '#fff', fontWeight: 700 }}>{formatOfferCurrency(currentPendingOffer.crew_payout)}</span>&nbsp;offer pending
                  </div>
                )}
              </div>
            </div>
            <div className="sz-hide-mobile" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
              <NotificationDropdown align="right" />
              <button type="button" onClick={() => navigate('/post-job')} className="cd-btn cd-btn-primary" style={{ padding: '13px 22px', fontSize: 14 }}>
                <Plus style={{ width: 18, height: 18 }} />
                Post a job
              </button>
              <div
                onClick={() => navigate('/profile')}
                title="Your profile"
                style={{ width: 46, height: 46, borderRadius: 999, overflow: 'hidden', cursor: 'pointer', flex: '0 0 auto', background: 'linear-gradient(135deg,#572B7C,#28C0D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 3px rgba(40,192,208,.2)' }}
              >
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Your profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span className="cd-display" style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{dashInitials}</span>
                )}
              </div>
            </div>
          </div>

          {/* HERO PENDING OFFER (urgent amber) */}
          {hasPendingOffer && (
            <div
              className="sz-col-mobile"
              style={{ position: 'relative', borderRadius: 14, padding: '28px 30px 28px 34px', marginTop: 24, background: 'linear-gradient(120deg,rgba(180,83,9,.32),rgba(124,45,18,.16) 48%,rgba(87,43,124,.18))', border: '1px solid rgba(252,211,77,.4)', boxShadow: '0 0 0 1px rgba(252,211,77,.12),0 26px 64px -28px rgba(180,83,9,.55)', overflow: 'hidden', display: 'flex', gap: 30, alignItems: 'center' }}
            >
              <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: 'linear-gradient(180deg,#FCD34D,#F59E0B)' }} />
              <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 13, flexWrap: 'wrap' }}>
                  <span className="cd-display" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#FCD34D' }}>
                    <AlertTriangle style={{ width: 13, height: 13 }} />ACTION NEEDED · PENDING OFFER
                  </span>
                  {offerTime && !offerTime.expired && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#0C1120', background: '#FCD34D', padding: '5px 11px', borderRadius: 999 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: '#92400E' }} />Expires in {offerTime.hours}h {offerTime.minutes}m
                    </span>
                  )}
                  {offerTime && offerTime.expired && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#fff', background: 'rgba(185,28,28,.5)', padding: '5px 11px', borderRadius: 999 }}>Expired</span>
                  )}
                </div>
                <div className="cd-display" style={{ fontWeight: 700, fontSize: 28, color: '#fff', letterSpacing: '-.7px' }}>{currentPendingOffer.job_title}</div>
                <div style={{ fontSize: 14.5, color: 'rgba(255,255,255,.72)', marginTop: 4 }}>from <span style={{ color: '#fff', fontWeight: 600 }}>{currentPendingOffer.owner_name}</span></div>
                <div style={{ display: 'flex', gap: 24, marginTop: 20, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.85)', fontSize: 13.5 }}>
                    <Calendar style={{ width: 16, height: 16, color: '#28C0D0' }} />{formatOfferDate(currentPendingOffer.job_date)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.85)', fontSize: 13.5 }}>
                    <Clock style={{ width: 16, height: 16, color: '#28C0D0' }} />{currentPendingOffer.hours} hours
                  </div>
                  {currentPendingOffer.job_location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.85)', fontSize: 13.5 }}>
                      <MapPin style={{ width: 16, height: 16, color: '#28C0D0' }} />{currentPendingOffer.job_location}
                    </div>
                  )}
                </div>
              </div>
              <div className="sz-hide-mobile" style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,.15)', position: 'relative', zIndex: 1 }} />
              <div className="sz-w-full-m" style={{ flex: '0 0 auto', width: 270, position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.62)' }}>Your payout <span style={{ opacity: .7 }}>(after 2% fee)</span></div>
                <div className="cd-display" style={{ fontWeight: 800, fontSize: 46, color: '#fff', letterSpacing: '-1.6px', lineHeight: 1.08 }}>{formatOfferCurrency(currentPendingOffer.crew_payout)}</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginBottom: 18 }}>{formatOfferCurrency(currentPendingOffer.pay_rate)}/hr × {currentPendingOffer.hours} hrs</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setShowPendingOfferModal(true)}
                    disabled={offerActionLoading}
                    style={{ flex: 1, padding: 13, borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,255,255,.26)', color: '#fff', fontFamily: 'var(--sz-font-body)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPendingOfferModal(true); handleAcceptOffer(); }}
                    disabled={offerActionLoading || (offerTime && offerTime.expired)}
                    style={{ flex: 1.5, padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#FCD34D,#F59E0B)', border: 'none', color: '#42210B', fontFamily: 'var(--sz-font-body)', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', boxShadow: '0 12px 28px -10px rgba(245,158,11,.7)' }}
                  >
                    {offerActionLoading ? 'Processing…' : 'Accept offer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SHOOTING FORECAST / WEATHER */}
          {weather && (
            <div style={{ marginTop: 18, borderRadius: 16, background: weatherReady ? `${weatherBg(weatherToday.weatherCode)}, rgba(255,255,255,.045)` : 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '18px 22px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(40,192,208,.16)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    <Sun style={{ width: 20, height: 20 }} />
                  </span>
                  <div>
                    <div style={{ fontFamily: 'Poppins, var(--sz-font-display), sans-serif', fontWeight: 700, fontSize: 17, color: '#fff', letterSpacing: '-.3px' }}>What&rsquo;s the weather?</div>
                    <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 1 }}>Light &amp; conditions for your upcoming shoots</div>
                  </div>
                </div>
                {weatherReady && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'rgba(255,255,255,.55)', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', padding: '7px 13px', borderRadius: 999 }}>
                    <MapPin style={{ width: 14, height: 14, color: '#28C0D0' }} />
                    {weather.location}
                  </div>
                )}
              </div>

              {weatherReady ? (
                <div className="sz-col-mobile" style={{ display: 'flex', gap: 0, padding: '18px 22px 20px' }}>
                  {/* Featured today */}
                  <div className="sz-wx-today" style={{ flex: '0 0 308px', paddingRight: 24, borderRight: '1px solid rgba(255,255,255,.08)', position: 'relative', overflow: 'hidden' }}>
                    <style>{`
                      @keyframes wxrain { from { transform: translateY(-40px); } to { transform: translateY(360px); } }
                      @keyframes wxsnow { from { transform: translateY(-40px) translateX(0); } to { transform: translateY(360px) translateX(14px); } }
                      @keyframes wxsun { 0%,100% { opacity:.45; transform: scale(1); } 50% { opacity:.8; transform: scale(1.08); } }
                      @keyframes wxcloud { from { transform: translateX(-20px); } to { transform: translateX(360px); } }
                      @media (prefers-reduced-motion: reduce) { .wx-p { animation: none !important; opacity: 0 !important; } }
                    `}</style>
                    {(() => {
                      const c = weatherToday.weatherCode;
                      const rain = (c >= 51 && c <= 67) || (c >= 80 && c <= 82) || c >= 95;
                      const snow = (c >= 71 && c <= 77) || c === 85 || c === 86;
                      const clear = c <= 1;
                      const cloudy = c === 2 || c === 3 || (c >= 45 && c <= 48);
                      if (rain) return (
                        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.3 }}>
                          {Array.from({ length: 38 }).map((_, i) => (
                            <span key={i} className="wx-p" style={{ position: 'absolute', top: 0, left: `${(i * 2.7) % 100}%`, width: 2, height: 17, borderRadius: 2, background: 'linear-gradient(rgba(175,208,255,0),rgba(195,222,255,.95))', animation: `wxrain ${0.55 + (i % 5) * 0.12}s linear ${(i % 8) * 0.09}s infinite` }} />
                          ))}
                        </div>
                      );
                      if (snow) return (
                        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
                          {Array.from({ length: 20 }).map((_, i) => (
                            <span key={i} className="wx-p" style={{ position: 'absolute', top: 0, left: `${(i * 5) % 100}%`, width: 4, height: 4, borderRadius: 999, background: 'rgba(220,240,255,.7)', animation: `wxsnow ${2.6 + (i % 4) * 0.5}s linear ${(i % 6) * 0.3}s infinite` }} />
                          ))}
                        </div>
                      );
                      if (clear) return (
                        <div aria-hidden="true" className="wx-p" style={{ position: 'absolute', top: -50, left: -40, width: 160, height: 160, borderRadius: 999, background: 'radial-gradient(closest-side, rgba(252,211,77,.5), transparent 70%)', filter: 'blur(6px)', pointerEvents: 'none', zIndex: 0, animation: 'wxsun 5s ease-in-out infinite' }} />
                      );
                      if (cloudy) return (
                        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.4 }}>
                          {[0, 1, 2].map((i) => (
                            <span key={i} className="wx-p" style={{ position: 'absolute', top: `${8 + i * 26}%`, left: '-35%', width: 140 - i * 26, height: 48 - i * 9, borderRadius: 999, background: 'radial-gradient(closest-side, rgba(205,214,232,.55), transparent 72%)', filter: 'blur(5px)', animation: `wxcloud ${24 + i * 9}s linear ${i * 5}s infinite` }} />
                          ))}
                        </div>
                      );
                      return null;
                    })()}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, position: 'relative', zIndex: 1 }}>
                      <span style={{ fontFamily: 'Poppins, var(--sz-font-display), sans-serif', fontWeight: 700, fontSize: 10.5, letterSpacing: '1.4px', color: '#28C0D0' }}>
                        TODAY · {new Date(weatherToday.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase()}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: weatherLightTag.fg, background: `${weatherLightTag.fg}29`, padding: '3px 9px', borderRadius: 999 }}>
                        {weatherLightTag.text}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                      <WeatherTodayIcon style={{ width: 58, height: 58, color: '#FCD34D', flex: '0 0 auto' }} strokeWidth={1.7} />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                          <span style={{ fontFamily: 'Poppins, var(--sz-font-display), sans-serif', fontWeight: 800, fontSize: 44, color: '#fff', letterSpacing: '-2px', lineHeight: .9 }}>{weatherToday.tempMax}</span>
                          <span style={{ fontFamily: 'Poppins, var(--sz-font-display), sans-serif', fontWeight: 700, fontSize: 18, color: 'rgba(255,255,255,.6)', marginTop: 3 }}>°C</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginTop: 4 }}>{weatherToday.condition || getWeatherLabel(weatherToday.weatherCode)} · low {weatherToday.tempMin}°</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16, position: 'relative', zIndex: 1 }}>
                      {weatherToday.goldenHour && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#fff', background: 'rgba(252,211,77,.12)', border: '1px solid rgba(252,211,77,.25)', padding: '6px 11px', borderRadius: 10 }}>
                          <Sun style={{ width: 13, height: 13, color: '#FCD34D', filter: 'drop-shadow(0 0 4px rgba(252,211,77,.7))' }} />
                          Golden hour {formatWeatherTime(weatherToday.goldenHour)}
                        </span>
                      )}
                      {weatherToday.sunset && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#fff', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', padding: '6px 11px', borderRadius: 10 }}>
                          <Sunset style={{ width: 13, height: 13, color: '#C98AD4' }} />
                          Sunset {formatWeatherTime(weatherToday.sunset)}
                        </span>
                      )}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#fff', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', padding: '6px 11px', borderRadius: 10 }}>
                        <Droplets style={{ width: 13, height: 13, color: '#60A5FA' }} />
                        {weatherToday.rainChance}% chance of rain
                      </span>
                    </div>
                  </div>

                  {/* 5-day light strip */}
                  <div className="sz-w-full-m sz-wx-days" style={{ flex: 1, minWidth: 0, paddingLeft: 24, display: 'flex', alignItems: 'stretch', gap: 8 }}>
                    {weather.days.slice(1).map((day, i) => {
                      const DayIcon = getWeatherLucide(day.weatherCode);
                      const isToday = false;
                      return (
                        <div key={day.date} style={{ flex: 1, textAlign: 'center', borderRadius: 13, background: isToday ? 'rgba(40,192,208,.07)' : 'rgba(255,255,255,.04)', border: isToday ? '1px solid rgba(40,192,208,.25)' : '1px solid rgba(255,255,255,.08)', padding: '14px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                          <span style={{ fontSize: 13.5, fontWeight: isToday ? 700 : 600, color: isToday ? '#28C0D0' : 'rgba(255,255,255,.6)' }}>{day.stripDay}</span>
                          <DayIcon style={{ width: 32, height: 32, color: '#9CA8C0' }} strokeWidth={1.8} />
                          <span style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>{day.tempMax}°</span>
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,.6)', lineHeight: 1.25, textAlign: 'center', minHeight: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{day.condition || getWeatherLabel(day.weatherCode)}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'rgba(255,255,255,.55)' }} title={`${day.rainChance}% chance of rain`}>
                            <span style={{ width: 7, height: 7, borderRadius: 999, background: weatherLightColor(day.rainChance) }} />
                            {day.rainChance}% rain
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* No coordinates — graceful fallback */
                <div style={{ padding: '20px 22px 22px', display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,.6)', fontSize: 13.5 }}>
                  <MapPin style={{ width: 16, height: 16, color: '#28C0D0', flex: '0 0 auto' }} />
                  Set your location in Profile to see shoot weather.
                </div>
              )}
            </div>
          )}

          {/* STATS — hidden */}
          {false && (
          <div className="sz-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 15, marginTop: 18 }}>
            {/* Applications */}
            <div className="cd-stat" onClick={() => navigate('/applications')} style={{ position: 'relative', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 17, padding: '19px 19px 17px', overflow: 'hidden', cursor: 'pointer' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#28C0D0,transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(40,192,208,.16)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText style={{ width: 18, height: 18 }} /></span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, color: '#fff', marginTop: 14, letterSpacing: '-1px', lineHeight: 1 }}>{loading ? '…' : applicationsCount}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>Applications out</div>
            </div>
            {/* Upcoming jobs */}
            <div className="cd-stat" onClick={() => navigate('/upcoming-bookings')} style={{ position: 'relative', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 17, padding: '19px 19px 17px', overflow: 'hidden', cursor: 'pointer' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#67C6B4,transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(103,198,180,.16)', color: '#67C6B4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Calendar style={{ width: 18, height: 18 }} /></span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, color: '#fff', marginTop: 14, letterSpacing: '-1px', lineHeight: 1 }}>{loading ? '…' : upcomingCount}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>Confirmed jobs</div>
            </div>
            {/* Job posts */}
            <div className="cd-stat" onClick={() => navigate('/hire-crew')} style={{ position: 'relative', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 17, padding: '19px 19px 17px', overflow: 'hidden', cursor: 'pointer' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#C98AD4,transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(120,40,124,.24)', color: '#C98AD4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Briefcase style={{ width: 18, height: 18 }} /></span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, color: '#fff', marginTop: 14, letterSpacing: '-1px', lineHeight: 1 }}>{loading ? '…' : jobPostsCount}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>Job posts</div>
            </div>
            {/* Crew network */}
            <div className="cd-stat" style={{ position: 'relative', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 17, padding: '19px 19px 17px', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,rgba(255,255,255,.6),transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(255,255,255,.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users style={{ width: 18, height: 18 }} /></span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>since {stats?.member_since || 'N/A'}</span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, color: '#fff', marginTop: 14, letterSpacing: '-1px', lineHeight: 1 }}>{loading ? '…' : crewNetworkCount.toLocaleString()}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', marginTop: 5 }}>Crew across Australia</div>
            </div>
          </div>
          )}

          {/* DUAL ACTIONS */}
          <div className="sz-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <div className="cd-action" onClick={() => navigate('/find-jobs')} style={{ borderRadius: 17, padding: '24px 26px', background: 'linear-gradient(120deg,rgba(40,192,208,.18),rgba(40,192,208,.04))', border: '1px solid rgba(40,192,208,.26)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <div>
                <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>Find your next gig</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.62)', marginTop: 4 }}>{applicationsCount} application{applicationsCount === 1 ? '' : 's'} out · browse live jobs</div>
              </div>
              <span style={{ width: 48, height: 48, borderRadius: 14, background: '#28C0D0', color: '#0C1120', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', boxShadow: '0 10px 24px -10px rgba(40,192,208,.7)' }}><ArrowRight style={{ width: 22, height: 22 }} /></span>
            </div>
            <div className="cd-action" onClick={() => navigate('/hire-crew')} style={{ borderRadius: 17, padding: '24px 26px', background: 'linear-gradient(120deg,rgba(120,40,124,.3),rgba(87,43,124,.08))', border: '1px solid rgba(120,40,124,.34)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
              <div>
                <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>Build your crew</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.62)', marginTop: 4 }}>{jobPostsCount} job post{jobPostsCount === 1 ? '' : 's'} · post &amp; hire crew</div>
              </div>
              <span style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg,#572B7C,#78287C)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', boxShadow: '0 10px 24px -10px rgba(120,40,124,.7)' }}><ArrowRight style={{ width: 22, height: 22 }} /></span>
            </div>
          </div>
        </div>
      </div>

      {/* System Message Modal */}
      {showSystemModal && systemModalMessages.length > 0 && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-3xl">📢</span>
              </div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {systemModalMessages[0].subject}
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                {systemModalMessages[0].message}
              </p>
              {systemModalMessages.length > 1 && (
                <p className="text-xs text-gray-400 mt-4">
                  {systemModalMessages.length - 1} more message{systemModalMessages.length - 1 > 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="px-6 pb-6">
              <Button
                onClick={handleDismissSystemModal}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              >
                {systemModalMessages.length > 1 ? 'Next' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showReviewModal && currentReview && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          job={currentReview}
          onSubmitSuccess={handleReviewSubmitSuccess}
        />
      )}

      {/* Crew Review Modal - for crew members to review job owners */}
      {showCrewReviewModal && currentCrewReview && (
        <CrewReviewModal
          isOpen={showCrewReviewModal}
          onClose={() => setShowCrewReviewModal(false)}
          pendingReview={currentCrewReview}
          userId={user.id}
          onSubmitSuccess={handleCrewReviewSubmitSuccess}
          isDark={isDark}
        />
      )}

      {showABNModal && (
        <ABNValidationModal
          userId={user.id}
          isOpen={showABNModal}
          onClose={() => setShowABNModal(false)}
          onValidated={handleABNValidated}
          isDark={isDark}
        />
      )}

      {/* Welcome Modal for NEW users */}
      <WelcomeModal
        show={showWelcomeModal}
        userName={user.first_name || user.firstName || user.full_name?.split(' ')[0] || user.fullName?.split(' ')[0]}
        onClose={() => setShowWelcomeModal(false)}
        destination="/onboarding"
        userId={user.id}
      />

      {/* Pending Approval Modal for PENDING users */}
      <PendingApprovalModal
        show={showPendingApprovalModal}
        userEmail={user.email}
        onClose={() => setShowPendingApprovalModal(false)}
      />

      {/* Cancellation Payment Blocker Modal */}
      <CancellationPaymentBlockerModal
        isOpen={showCancellationBlocker}
        outstandingFees={outstandingCancellationFees}
      />

      {/* Pending Offer Modal */}
      {showPendingOfferModal && currentPendingOffer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300 my-auto sm:my-4 max-h-[95vh] sm:max-h-[90vh] flex flex-col">

            {/* Success State - Offer Accepted */}
            {offerAccepted && (
              <>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 sm:p-8 text-center flex-shrink-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                    Congratulations!
                  </h2>
                  <p className="text-green-100 text-sm sm:text-base">
                    This job is now confirmed
                  </p>
                </div>
                <div className="p-6 sm:p-8 text-center">
                  <p className="text-gray-700 text-sm sm:text-base mb-4">
                    Please check your email for a confirmation message.
                  </p>
                  <p className="text-gray-600 text-sm mb-6">
                    You can also find job details in the Upcoming Bookings page.
                  </p>
                  <Button
                    onClick={handleAcceptedModalClose}
                    className="w-full h-12 sm:h-14 text-sm sm:text-base font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg rounded-xl"
                  >
                    View Upcoming Bookings
                  </Button>
                </div>
              </>
            )}

            {/* Success State - Offer Declined */}
            {offerDeclined && !offerAccepted && (
              <>
                <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-6 sm:p-8 text-center flex-shrink-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                    Offer Declined
                  </h2>
                  <p className="text-gray-200 text-sm sm:text-base">
                    This offer has been declined
                  </p>
                </div>
                <div className="p-6 sm:p-8 text-center">
                  <p className="text-gray-700 text-sm sm:text-base mb-4">
                    The job owner has been notified.
                  </p>
                  <p className="text-gray-600 text-sm mb-6">
                    Don't worry - there are plenty of other great opportunities available!
                  </p>
                  <Button
                    onClick={handleDeclinedModalClose}
                    className="w-full h-12 sm:h-14 text-sm sm:text-base font-bold bg-gradient-to-r from-purple-500 to-fuchsia-600 hover:from-purple-600 hover:to-fuchsia-700 text-white shadow-lg rounded-xl"
                  >
                    Close
                  </Button>
                </div>
              </>
            )}

            {/* Default State - Show Offer Details */}
            {!offerAccepted && !offerDeclined && (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 p-4 sm:p-6 text-center relative flex-shrink-0">
                  <button
                    onClick={() => setShowPendingOfferModal(false)}
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                    You Have a Job Offer!
                  </h2>
                  <p className="text-purple-100 text-xs sm:text-sm">
                    Review the details and respond below
                  </p>
                </div>

                {/* Offer Details */}
                <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
                  {/* Job Title */}
                  <div className="text-center pb-3 sm:pb-4 border-b border-gray-100">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800">{currentPendingOffer.job_title}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">from {currentPendingOffer.owner_name}</p>
                    {/* Show role if present */}
                    {currentPendingOffer.role && (
                      <div className="mt-2 inline-flex items-center bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold capitalize">
                        Role: {currentPendingOffer.role.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>

                  {/* Job Info Grid */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Date</p>
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">{formatOfferDate(currentPendingOffer.job_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Duration</p>
                        <p className="text-xs sm:text-sm font-semibold text-gray-800">{currentPendingOffer.hours} hours</p>
                      </div>
                    </div>
                  </div>

                  {currentPendingOffer.job_location && (
                    <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Location</p>
                        <p className="text-xs sm:text-sm font-semibold text-gray-800 break-words">{currentPendingOffer.job_location}</p>
                      </div>
                    </div>
                  )}

                  {/* Your Payout */}
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] sm:text-xs text-gray-500 font-medium">Your Payout</p>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">{formatOfferCurrency(currentPendingOffer.pay_rate)}/hr × {currentPendingOffer.hours}hrs</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl sm:text-2xl font-bold text-green-600">{formatOfferCurrency(currentPendingOffer.crew_payout)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Offer Expires */}
                  {(() => {
                    const timeRemaining = getOfferTimeRemaining(currentPendingOffer.expires_at);
                    return (
                      <div className={`${timeRemaining.expired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg sm:rounded-xl p-2 sm:p-3 flex items-center gap-2 sm:gap-3`}>
                        <AlertCircle className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${timeRemaining.expired ? 'text-red-600' : 'text-amber-600'}`} />
                        <div className="text-xs sm:text-sm">
                          {timeRemaining.expired ? (
                            <span className="font-semibold text-red-900">This offer has expired</span>
                          ) : (
                            <>
                              <span className="font-semibold text-amber-900">Offer expires in </span>
                              <span className="text-amber-700">{timeRemaining.hours}h {timeRemaining.minutes}m</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Info Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-700 font-semibold mb-1 sm:mb-2">By accepting this offer:</p>
                    <ul className="text-[10px] sm:text-xs text-gray-600 space-y-0.5 sm:space-y-1">
                      <li>• You commit to working this job on the scheduled date</li>
                      <li>• Payment will be held securely until job completion</li>
                      <li>• You'll receive the job owner's contact details</li>
                      <li>• You agree to the Shootzu <a href="https://shootzu.com/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">Terms and Conditions</a></li>
                    </ul>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 sm:p-6 pt-3 sm:pt-4 space-y-2 sm:space-y-3 flex-shrink-0 border-t border-gray-100">
                  <Button
                    onClick={handleAcceptOffer}
                    disabled={offerActionLoading || getOfferTimeRemaining(currentPendingOffer.expires_at).expired}
                    className="w-full h-12 sm:h-14 text-sm sm:text-base font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg rounded-xl"
                  >
                    {offerActionLoading ? (
                      <>
                        <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Accept This Offer
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleDeclineOffer}
                    disabled={offerActionLoading}
                    variant="outline"
                    className="w-full h-10 sm:h-12 text-xs sm:text-sm font-medium border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl"
                  >
                    Decline Offer
                  </Button>
                  <button
                    onClick={() => setShowPendingOfferModal(false)}
                    className="w-full text-xs sm:text-sm text-gray-500 hover:text-gray-700 py-1.5 sm:py-2"
                  >
                    Remind Me Later
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardRedesigned;
